import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, ArrowLeft, Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLS = [
  ["customer_name", "Customer"],
  ["disposition_name", "Disposition"],
  ["attempt_datetime", "Attempt"],
  ["agent_name", "Agent"],
  ["registration_date", "Reg date (DB)"],
  ["last_deposit_date_today", "Last dep (today)"],
  ["inactive_days_today", "Inactive days"],
  ["credit_status", "Credit"],
  ["credit_reason", "Reason"],
];

export default function OpsUploadDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => api.get(`/ops/uploads/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/ops/uploads/${id}/refresh-enrichment`);
      toast.success(`Re-enriched ${r.data.refreshed} rows`);
      load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const download = async () => {
    const r = await api.get(`/ops/uploads/${id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `enriched_${data?.meta?.brand_name || "export"}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const meta = data.meta;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/ops/uploads" className="text-xs text-slate-500 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <h1 className="font-outfit font-semibold text-2xl md:text-3xl text-slate-900">
            {meta.brand_name} — {new Date(meta.uploaded_at).toLocaleDateString()}
          </h1>
          <div className="text-xs text-slate-500 mt-1">{meta.row_count} rows · uploaded by {meta.ops_user_email}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="ops-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh enrichment
          </Button>
          <Button onClick={download} className="btn-tactile" data-testid="ops-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download enriched Excel
          </Button>
        </div>
      </div>

      {/* Row-math panel — explains any gap between file and DB row counts */}
      {meta.total_in_file != null && (
        <div className="glass-card p-3 flex flex-wrap gap-x-6 gap-y-1 text-xs" data-testid="upload-row-math">
          <div><span className="text-slate-500">Rows in file:</span> <span className="font-semibold">{meta.total_in_file}</span></div>
          <div><span className="text-slate-500">Skipped (blank / bad datetime):</span>{" "}
            <span className={`font-semibold ${meta.skipped_blank > 0 ? "text-amber-700" : ""}`}>{meta.skipped_blank ?? 0}</span></div>
          <div><span className="text-slate-500">Deduped in-file:</span>{" "}
            {(() => {
              const deduped = Math.max(0, (meta.total_in_file - (meta.skipped_blank || 0)) - meta.row_count);
              return <span className={`font-semibold ${deduped > 0 ? "text-amber-700" : ""}`}>{deduped}</span>;
            })()}
          </div>
          <div><span className="text-slate-500">Stored in DB:</span> <span className="font-semibold text-emerald-700">{meta.row_count}</span></div>
          {meta.inserted != null && (
            <>
              <div className="w-full mt-1"></div>
              <div><span className="text-slate-500">Newly inserted:</span> <span className="font-semibold">{meta.inserted}</span></div>
              <div><span className="text-slate-500">Updated / overwritten:</span> <span className="font-semibold">{meta.updated}</span></div>
              <div className="text-[10px] text-slate-500 w-full mt-1">
                &quot;Updated&quot; = rows that matched an existing key
                (<code>brand · customer · attempt_datetime</code>) — either the same call was already ingested from a
                previous upload, or the file contained duplicate rows for the same key.
              </div>
            </>
          )}
        </div>
      )}

      <div className="glass-card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500">
              {COLS.map(([k, h]) => <th key={k} className="text-left py-2 px-2 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-white/50 hover:bg-white/40">
                {COLS.map(([k]) => (
                  <td key={k} className="py-1.5 px-2 whitespace-nowrap">
                    {k === "credit_status" ? (
                      <span className={`px-1.5 py-0.5 rounded text-[11px] ${r[k] === "yes" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                        {r[k]}
                      </span>
                    ) : (r[k] === null || r[k] === undefined ? "—" : String(r[k]))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
